import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Heart, MapPin, ExternalLink, Trophy, Search } from 'lucide-react';
import { api, friendlyError, unwrap, setPrimaryFavoriteClub } from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { EmptyState } from '../EmptyState';

export function MyClubTab() {
  const { user, fetchMe } = useAuth();
  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      // Lista de favoritos del usuario
      const favsRes = await api.get('/users/me/favorites');
      const items = (unwrap(favsRes)?.items) || [];
      setFavorites(items);
      // Resolver club favorito principal
      const favId = user?.favorite_club_id || items[0]?.club_id;
      if (favId) {
        // Buscar el slug en la lista de favoritos
        const favEntry = items.find((x) => x.club_id === favId) || items[0];
        const slug = favEntry?.club_slug;
        if (slug) {
          try {
            const cRes = await api.get(`/clubs/${encodeURIComponent(slug)}`);
            setClub(unwrap(cRes));
          } catch {
            setClub({ id: favEntry.club_id, name: favEntry.club_name, slug: favEntry.club_slug });
          }
        } else if (favEntry) {
          setClub({ id: favEntry.club_id, name: favEntry.club_name, slug: favEntry.club_slug });
        } else {
          setClub(null);
        }
      } else {
        setClub(null);
      }
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user?.favorite_club_id]);

  const setAsPrimary = async (clubId) => {
    if (busyId) return;
    setBusyId(clubId);
    try {
      await setPrimaryFavoriteClub(clubId);
      await fetchMe();
      toast.success('Club favorito actualizado.');
      await reload();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 rounded-2xl bg-white border border-[var(--ns-border)]">
        <Skeleton className="h-7 w-48 mb-4" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
        <SectionTitle icon={Heart} label="Mi club favorito" hint="Tu club destacado en el dashboard." />
        {club ? (
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {club.logo_url ? (
                <img
                  src={club.logo_url}
                  alt={club.name}
                  className="h-20 w-20 rounded-xl object-contain bg-[var(--ns-bg-2)] p-2 border border-[var(--ns-border)] shrink-0"
                />
              ) : (
                <div className="h-20 w-20 rounded-xl bg-[var(--ns-bg-2)] inline-flex items-center justify-center shrink-0">
                  <Trophy className="h-8 w-8 text-[var(--ns-gold-700)]" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-display uppercase text-2xl tracking-wide text-[var(--ns-text)] truncate">
                  {club.name}
                </p>
                {club.city ? (
                  <p className="text-sm text-[var(--ns-text-muted)] mt-0.5 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {club.city}
                  </p>
                ) : null}
                {club.sport_name ? (
                  <Badge className="mt-2 bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)]">
                    {club.sport_name}
                  </Badge>
                ) : null}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                asChild
                data-testid={DASHBOARD.clubLink}
                className="h-10 px-4 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0"
              >
                <Link to={`/clubes/${club.slug || club.id}`} className="inline-flex items-center gap-1">
                  Ir al club
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={Heart}
            title="Aún no tienes club favorito"
            description="Sigue un club para descubrir antes que nadie sus partidos y contenidos."
            ctaLabel="Explorar clubes"
            ctaTo="/clubes"
            testId={DASHBOARD.clubEmpty}
          />
        )}
      </Card>

      {/* Lista de favoritos (si tiene más de uno) */}
      {favorites.length > 1 ? (
        <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
          <SectionTitle
            label="Otros clubes que sigues"
            hint="Marca uno como principal para verlo destacado."
          />
          <ul className="space-y-2">
            {favorites
              .filter((f) => f.club_id !== (user?.favorite_club_id || club?.id))
              .map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl border border-[var(--ns-border)] hover:bg-[var(--ns-bg-2)]/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{f.club_name}</p>
                    <p className="text-xs text-[var(--ns-text-muted)]">
                      Desde {f.created_at ? new Date(f.created_at).toLocaleDateString('es-ES') : '—'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === f.club_id}
                    onClick={() => setAsPrimary(f.club_id)}
                    data-testid={DASHBOARD.clubChange}
                    className="h-8 px-3 rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)] text-xs"
                  >
                    Marcar como principal
                  </Button>
                </li>
              ))}
          </ul>
          <Button
            variant="ghost"
            onClick={() => navigate('/clubes')}
            className="mt-3 h-9 text-[var(--ns-gold-700)] hover:text-[var(--ns-gold)] hover:bg-[var(--ns-amber-50)]"
          >
            <Search className="h-3.5 w-3.5 mr-1" />
            Buscar más clubes
          </Button>
        </Card>
      ) : club ? (
        <Card className="p-5 rounded-2xl bg-white border border-[var(--ns-border)]">
          <Button
            variant="outline"
            onClick={() => navigate('/clubes')}
            className="h-10 rounded-xl bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)] w-full sm:w-auto"
          >
            <Search className="h-3.5 w-3.5 mr-1" />
            Descubrir más clubes
          </Button>
        </Card>
      ) : null}
    </div>
  );
}
